import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Search, FileText, Clock, Eye, CheckCircle2, XCircle, User, Mail, Calendar, MapPin, Briefcase, Download, Filter, RefreshCw, MoreVertical, FileDown, FileCheck, FileOutput } from 'lucide-react';
import { toast } from 'sonner';
import { apiClient } from '../../lib/api-client';
import { ImageWithFallback } from '../ui/ImageWithFallback';
import { normalizeMediaUrl } from '../../utils/media';

interface ApplicationsManagementProps {
  applications: any[];
  onApplicationUpdate: (applications: any[]) => void;
}

export function ApplicationsManagement({ applications: initialApplications, onApplicationUpdate }: ApplicationsManagementProps) {
  const [applications, setApplications] = useState<any[]>(initialApplications || []);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCompany, setFilterCompany] = useState<string>('');
  const [jobFilter, setJobFilter] = useState<string>('');
  const [jobCategoryFilter, setJobCategoryFilter] = useState<'all' | 'active' | 'expired'>('all');
  const [selectedApplication, setSelectedApplication] = useState<any>(null);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Load applications from backend
  useEffect(() => {
    loadApplications();
  }, [filterStatus]);

  const loadApplications = async () => {
    setIsLoading(true);
    try {
      const params: any = { limit: 500 };
      if (filterStatus !== 'all') {
        params.status = filterStatus;
      }
      const res = await apiClient.getAdminApplications(params);
      const items = Array.isArray(res?.applications) ? res.applications : [];
      const mapped = items.map((app: any) => ({
        id: app._id || app.id,
        jobId: app.job_id || app.jobId,
        userId: app.job_seeker_id || app.userId,
        jobTitle: app.job?.title || app.jobTitle || 'N/A',
        company: app.job?.company || app.company || 'N/A',
        applicantName: app.user?.full_name || app.user?.name || app.applicantName || 'Unknown',
        applicantEmail: app.user?.email || app.applicantEmail || 'N/A',
        status: app.status || 'pending',
        appliedAt: app.applied_at || app.appliedAt || new Date().toISOString(),
        coverLetter: app.cover_letter || app.coverLetter,
        resumeUrl: app.resume_url || app.resumeUrl,
        jobStatus: app.job?.status,
        jobDeadline: app.job?.deadline || app.job?.expiryDate || app.job?.expiry_date,
      }));
      setApplications(mapped);
      onApplicationUpdate(mapped);
    } catch (error) {
      console.error('Failed to load applications:', error);
      toast.error('Failed to load applications');
    } finally {
      setIsLoading(false);
    }
  };

  const filteredApplications = applications.filter(app => {
    const matchesSearch =
      app.applicantName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      app.applicantEmail?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      app.jobTitle?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      app.company?.toLowerCase().includes(searchTerm.toLowerCase());

    // Improved status matching logic
    let matchesStatus = true;
    if (filterStatus === 'all') {
      matchesStatus = true;
    } else if (filterStatus === 'pending') {
      matchesStatus = app.status === 'pending';
    } else if (filterStatus === 'reviewed') {
      matchesStatus = app.status === 'reviewed' || app.status === 'shortlisted';
    } else if (filterStatus === 'accepted') {
      matchesStatus = app.status === 'accepted' || app.status === 'hired';
    } else if (filterStatus === 'rejected') {
      matchesStatus = app.status === 'rejected';
    } else {
      matchesStatus = app.status === filterStatus;
    }

    const matchesJob = jobFilter === 'all' || String(app.jobId || app.jobTitle) === jobFilter;

    // Job category filter (Active vs Expired)
    let matchesJobCategory = true;
    if (jobCategoryFilter !== 'all') {
      const now = new Date();
      const isExpired = app.jobStatus === 'expired' || app.jobStatus === 'closed' || (
        app.jobDeadline && new Date(app.jobDeadline).getTime() <= now.getTime()
      );

      if (jobCategoryFilter === 'active') {
        matchesJobCategory = !isExpired;
      } else if (jobCategoryFilter === 'expired') {
        matchesJobCategory = isExpired;
      }
    }

    return matchesSearch && matchesStatus && matchesJob && matchesJobCategory;
  });

  console.log('📊 Admin Applications Filter:', {
    total: applications.length,
    filtered: filteredApplications.length,
    filterStatus,
    searchTerm
  });

  const handleStatusUpdate = async (applicationId: string, newStatus: 'pending' | 'reviewed' | 'accepted' | 'rejected') => {
    try {
      await apiClient.updateApplicationStatus(applicationId, newStatus);

      // Update local state
      const updated = applications.map(app =>
        app.id === applicationId ? { ...app, status: newStatus } : app
      );
      setApplications(updated);
      onApplicationUpdate(updated);

      toast.success(`Application status updated to ${newStatus}`);
    } catch (error) {
      console.error('Failed to update status:', error);
      toast.error('Failed to update application status');
    }
  };

  const handleViewDetails = (application: any) => {
    setSelectedApplication(application);
    setShowDetailsDialog(true);
  };

  const getStatusBadge = (status: string) => {
    const config: any = {
      pending: { color: 'bg-yellow-100 text-yellow-800 border-yellow-300', icon: Clock, label: 'Pending' },
      reviewed: { color: 'bg-blue-100 text-blue-800 border-blue-300', icon: Eye, label: 'Reviewed' },
      accepted: { color: 'bg-green-100 text-green-800 border-green-300', icon: CheckCircle2, label: 'Accepted' },
      rejected: { color: 'bg-red-100 text-red-800 border-red-300', icon: XCircle, label: 'Rejected' }
    };
    return config[status] || config.pending;
  };

  const stats = {
    total: applications.length,
    pending: applications.filter(a => a.status === 'pending').length,
    reviewed: applications.filter(a => a.status === 'reviewed').length,
    accepted: applications.filter(a => a.status === 'accepted').length,
    rejected: applications.filter(a => a.status === 'rejected').length
  };

  const handleDownloadCV = async (app: any, format: 'pdf' | 'docx') => {
    const toastId = toast.loading(`Preparing ${format.toUpperCase()}...`);
    try {
      const response = await apiClient.requestBlob(
        `/admin/candidates/${app.userId}/applications/${app.id}/export?format=${format}`
      );

      const url = window.URL.createObjectURL(response);
      const link = document.createElement('a');
      link.href = url;
      // The backend sets the filename, but we set it here as fallback
      link.setAttribute('download', `${app.applicantName.replace(/\s+/g, '_')}_CV.${format === 'docx' ? 'docx' : 'pdf'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${format.toUpperCase()} downloaded successfully`, { id: toastId });
    } catch (err: any) {
      console.error('Download error:', err);
      toast.error(`Failed to download ${format.toUpperCase()}: ${err.message || 'Server error'}`, { id: toastId });
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <div className="p-3 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg">
              <FileText className="h-8 w-8 text-white" />
            </div>
            Applications Management
          </h2>
          <p className="text-gray-500 mt-1">Review and manage job applications</p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            className="gap-2"
            onClick={() => {
              loadApplications();
              toast.success('Refreshing applications...');
            }}
            disabled={isLoading}
          >
            <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button
            variant="outline"
            className="gap-2"
            onClick={() => {
              // Export applications to CSV
              const csvContent = [
                ['Applicant Name', 'Email', 'Job Title', 'Company', 'Status', 'Applied Date'].join(','),
                ...filteredApplications.map(app => [
                  app.applicantName,
                  app.applicantEmail,
                  app.jobTitle,
                  app.company,
                  app.status,
                  new Date(app.appliedAt).toLocaleDateString()
                ].join(','))
              ].join('\n');

              const blob = new Blob([csvContent], { type: 'text/csv' });
              const url = window.URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `applications_${filterStatus}_${new Date().toISOString().split('T')[0]}.csv`;
              a.click();
              window.URL.revokeObjectURL(url);
              toast.success(`Exported ${filteredApplications.length} applications`);
            }}
          >
            <Download className="h-4 w-4" />
            Export
          </Button>
        </div>
      </div>

      {/* Stats Cards - Clickable for filtering */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-2">
        <Card
          className={`border-l-2 border-l-gray-400 cursor-pointer ${filterStatus === 'all' ? 'ring-1 ring-gray-400' : ''}`}
          onClick={() => setFilterStatus('all')}
        >
          <CardContent className="p-2">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[9px] uppercase font-bold text-gray-400 leading-none mb-1">Total</p>
                <p className="text-sm font-bold text-gray-900">{stats.total}</p>
              </div>
              <FileText className="h-3.5 w-3.5 text-gray-300" />
            </div>
          </CardContent>
        </Card>

        <Card
          className={`border-l-2 border-l-yellow-400 cursor-pointer ${filterStatus === 'pending' ? 'ring-1 ring-yellow-400' : ''}`}
          onClick={() => setFilterStatus('pending')}
        >
          <CardContent className="p-2">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[9px] uppercase font-bold text-gray-400 leading-none mb-1">Pending</p>
                <p className="text-sm font-bold text-gray-900">{stats.pending}</p>
              </div>
              <Clock className="h-3.5 w-3.5 text-yellow-400" />
            </div>
          </CardContent>
        </Card>

        <Card
          className={`border-l-2 border-l-blue-400 cursor-pointer ${filterStatus === 'reviewed' ? 'ring-1 ring-blue-400' : ''}`}
          onClick={() => setFilterStatus('reviewed')}
        >
          <CardContent className="p-2">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[9px] uppercase font-bold text-gray-400 leading-none mb-1">Reviewed</p>
                <p className="text-sm font-bold text-gray-900">{stats.reviewed}</p>
              </div>
              <Eye className="h-3.5 w-3.5 text-blue-400" />
            </div>
          </CardContent>
        </Card>

        <Card
          className={`border-l-2 border-l-green-400 cursor-pointer ${filterStatus === 'accepted' ? 'ring-1 ring-green-400' : ''}`}
          onClick={() => setFilterStatus('accepted')}
        >
          <CardContent className="p-2">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[9px] uppercase font-bold text-gray-400 leading-none mb-1">Accepted</p>
                <p className="text-sm font-bold text-gray-900">{stats.accepted}</p>
              </div>
              <CheckCircle2 className="h-3.5 w-3.5 text-green-400" />
            </div>
          </CardContent>
        </Card>

        <Card
          className={`border-l-2 border-l-red-400 cursor-pointer ${filterStatus === 'rejected' ? 'ring-1 ring-red-400' : ''}`}
          onClick={() => setFilterStatus('rejected')}
        >
          <CardContent className="p-2">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[9px] uppercase font-bold text-gray-400 leading-none mb-1">Rejected</p>
                <p className="text-sm font-bold text-gray-900">{stats.rejected}</p>
              </div>
              <XCircle className="h-3.5 w-3.5 text-red-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-2">
          <div className="flex flex-col lg:flex-row gap-2">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search applicants, jobs..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9 h-8 text-xs font-medium"
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <select
                value={filterCompany}
                onChange={(e) => {
                  setFilterCompany(e.target.value);
                  setJobFilter(''); // Reset job when company changes
                }}
                className="px-2 py-1 bg-white border border-gray-400 rounded h-8 text-[11px] font-bold min-w-[140px] focus:outline-none appearance-none cursor-pointer"
                style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' fill=\'none\' viewBox=\'0 0 24 24\' stroke=\'currentColor\'%3E%3Cpath stroke-linecap=\'round\' stroke-linejoin=\'round\' stroke-width=\'2\' d=\'M19 9l-7 7-7-7\' /%3E%3C/svg%3E")', backgroundRepeat: 'no-repeat', backgroundPosition: 'right 6px center', backgroundSize: '12px' }}
              >
                <option value="">Select Employer</option>
                {Array.from(new Set(applications.map(a => a.company))).filter(Boolean).sort().map(company => (
                  <option key={company} value={company}>{company}</option>
                ))}
              </select>

              <select
                value={jobFilter}
                onChange={(e) => setJobFilter(e.target.value)}
                disabled={!filterCompany}
                className={`px-2 py-1 bg-white border rounded h-8 text-[11px] font-bold min-w-[200px] focus:outline-none appearance-none cursor-pointer transition-colors ${!filterCompany ? 'opacity-50 border-gray-200' : 'border-gray-900 hover:bg-gray-50'}`}
                style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' fill=\'none\' viewBox=\'0 0 24 24\' stroke=\'currentColor\'%3E%3Cpath stroke-linecap=\'round\' stroke-linejoin=\'round\' stroke-width=\'2\' d=\'M19 9l-7 7-7-7\' /%3E%3C/svg%3E")', backgroundRepeat: 'no-repeat', backgroundPosition: 'right 6px center', backgroundSize: '12px' }}
              >
                <option value="">{filterCompany ? 'Select Vacancy' : 'Select Job'}</option>
                {Array.from(new Map(
                  applications
                    .filter(a => a.company === filterCompany)
                    .map(a => [String(a.jobId || a.jobTitle), { id: String(a.jobId || a.jobTitle), label: `${a.jobTitle}` }])
                ).values()).map(opt => (
                  <option key={opt.id} value={opt.id}>{opt.label}</option>
                ))}
              </select>

              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-2 py-1 bg-white border border-gray-200 rounded h-8 text-[11px] font-medium w-[120px] focus:outline-none appearance-none"
                style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' fill=\'none\' viewBox=\'0 0 24 24\' stroke=\'currentColor\'%3E%3Cpath stroke-linecap=\'round\' stroke-linejoin=\'round\' stroke-width=\'2\' d=\'M19 9l-7 7-7-7\' /%3E%3C/svg%3E")', backgroundRepeat: 'no-repeat', backgroundPosition: 'right 6px center', backgroundSize: '12px' }}
              >
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="reviewed">Reviewed</option>
                <option value="accepted">Accepted</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Applications Table - Only shown when a job is selected */}
      {jobFilter ? (
        <Card className="border-none shadow-sm overflow-hidden animate-in fade-in slide-in-from-top-2 duration-300">
          <CardHeader className="py-2 px-4 border-b border-gray-100 bg-white">
            <CardTitle className="text-sm font-bold text-gray-800">
              Applicants
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse bg-white">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50/30">
                    <th className="px-3 py-2 text-[11px] font-bold text-gray-500 uppercase tracking-tight">Applicant</th>
                    <th className="px-3 py-2 text-[11px] font-bold text-gray-500 uppercase tracking-tight">Contact</th>
                    <th className="px-3 py-2 text-[11px] font-bold text-gray-500 uppercase tracking-tight">Applied</th>
                    <th className="px-3 py-2 text-[11px] font-bold text-gray-500 uppercase tracking-tight text-center">Status</th>
                    <th className="px-3 py-2 text-[11px] font-bold text-gray-500 uppercase tracking-tight text-center">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredApplications.map((application) => {
                    return (
                      <tr key={application.id} className="border-b border-gray-50 bg-white hover:bg-gray-50/50 transition-colors">
                        <td className="px-3 py-1.5">
                          <span className="text-xs font-bold text-gray-900">{application.applicantName}</span>
                        </td>
                        <td className="px-3 py-1.5">
                          <span className="text-xs text-gray-500">{application.applicantEmail}</span>
                        </td>
                        <td className="px-3 py-1.5">
                          <span className="text-xs text-gray-500">
                            {new Date(application.appliedAt).toLocaleDateString()}
                          </span>
                        </td>
                        <td className="px-3 py-1.5 text-center">
                          <div className="inline-block px-2 py-0.5 text-[10px] text-gray-600 font-bold border border-gray-200 rounded">
                            {application.status}
                          </div>
                        </td>
                        <td className="px-3 py-1.5">
                          <div className="flex items-center justify-center gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleViewDetails(application)}
                              className="bg-white hover:bg-gray-50 text-gray-900 border border-gray-200 text-[10px] h-6 px-3 font-bold rounded shadow-none"
                            >
                              View
                            </Button>
                            <select
                              value={application.status}
                              onChange={(e) => handleStatusUpdate(application.id, e.target.value as any)}
                              className="h-6 pl-1 pr-4 text-[10px] bg-white border border-gray-200 text-gray-700 cursor-pointer focus:outline-none appearance-none font-bold rounded"
                              style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' fill=\'none\' viewBox=\'0 0 24 24\' stroke=\'currentColor\'%3E%3Cpath stroke-linecap=\'round\' stroke-linejoin=\'round\' stroke-width=\'2\' d=\'M19 9l-7 7-7-7\' /%3E%3C/svg%3E")', backgroundRepeat: 'no-repeat', backgroundPosition: 'right 3px center', backgroundSize: '10px' }}
                            >
                              <option value="pending">Pending</option>
                              <option value="reviewed">Reviewed</option>
                              <option value="accepted">Accepted</option>
                              <option value="rejected">Rejected</option>
                            </select>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {filteredApplications.length === 0 && (
                    <tr>
                      <td colSpan={5} className="py-20 text-center">
                        <FileText className="h-10 w-10 text-gray-200 mx-auto mb-3" />
                        <p className="text-gray-500 text-sm">No applicants found matching your criteria</p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-2 border-dashed border-gray-200 bg-gray-50/50">
          <CardContent className="py-20 flex flex-col items-center justify-center">
            <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-sm mb-4">
              <Briefcase className="h-8 w-8 text-gray-300" />
            </div>
            <h3 className="text-lg font-bold text-gray-900 mb-1">Select Employer & Job</h3>
            <p className="text-gray-500 text-sm text-center max-w-xs">
              First select an employer and their specific job vacancy from the filters above to view the applicant list.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Applicant Profile Dialog */}
      <Dialog open={showDetailsDialog} onOpenChange={setShowDetailsDialog}>
        <DialogContent className="max-w-xl bg-white p-6 border shadow-lg rounded-none overflow-hidden outline-none ring-0">
          {selectedApplication && (
            <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-200">
              {/* Header */}
              <div className="relative border-b-0 space-y-1">
                <h2 className="text-2xl font-bold text-gray-900">Applicant Profile</h2>
                <p className="text-gray-500 text-sm">Full profile and resume downloads</p>

                <div className="pt-4 space-y-0.5">
                  <p className="text-base font-semibold text-gray-900">{selectedApplication.applicantName}</p>
                  <p className="text-sm text-gray-600">{selectedApplication.applicantEmail}</p>
                  <p className="text-xs text-gray-400 font-normal">
                    {new Date(selectedApplication.appliedAt).toLocaleString()}
                  </p>
                </div>
              </div>

              {/* Profile Section */}
              <div className="space-y-1.5">
                <h3 className="text-xs font-bold text-gray-900 uppercase tracking-wider">Profile</h3>
                <div className="space-y-0.5">
                  <p className="text-base font-semibold text-gray-900">{selectedApplication.applicantName}</p>
                  <p className="text-sm text-gray-600">{selectedApplication.applicantEmail}</p>
                </div>
              </div>

              {/* Resumes Section */}
              <div className="space-y-3">
                <h3 className="text-xs font-bold text-gray-900 uppercase tracking-wider">Resumes</h3>
                <div className="border border-gray-200 p-4 flex items-center justify-between bg-white">
                  <div className="space-y-1">
                    <p className="text-sm font-bold text-gray-900">My Resume</p>
                    <div className="space-y-0">
                      <p className="text-gray-500 text-xs">Template: modern</p>
                      <p className="text-gray-400 text-[10px]">
                        Updated: {new Date(selectedApplication.appliedAt).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDownloadCV(selectedApplication, 'pdf')}
                      className="bg-white border-gray-300 hover:bg-gray-900 hover:text-white transition-all text-gray-900 px-4 h-9 text-xs font-bold rounded-none"
                    >
                      Download PDF
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDownloadCV(selectedApplication, 'docx')}
                      className="bg-white border-gray-300 hover:bg-gray-900 hover:text-white transition-all text-gray-900 px-4 h-9 text-xs font-bold rounded-none"
                    >
                      Download DOCX
                    </Button>
                  </div>
                </div>
              </div>

              {/* Cover Letter (if exists) */}
              {selectedApplication.coverLetter && (
                <div className="pt-4 border-t border-gray-50">
                  <h3 className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">Message</h3>
                  <div className="bg-gray-50 p-4 border-l-4 border-gray-900">
                    <p className="text-gray-700 text-sm leading-relaxed italic">
                      "{selectedApplication.coverLetter}"
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
